import Button from 'mcs-toolkit/components/Button';
import React from 'react';
import { ReduxTypeUtils } from 'utils/ReduxTypeUtils';

import { GlobalState } from 'reactApp/core/rootReducer';
import {
    BillingActions,
    BillingSelectors,
    getBillingActions,
} from 'reactApp/modules/BillingModule/BillingModule';
import { CurrentUserSelectors } from 'reactApp/modules/CurrentUserModule';
import { getActiveRouteParams } from 'reactApp/reducers/activeRoute';
import { Confirmator } from 'reactComponents/Confirmator/Confirmator';
import Dialog from 'reactComponents/dialog/Dialog';
import { DialogAsMethodRedererProps } from 'reactComponents/DialogAsMethodRederer/DialogAsMethodRederer';
import { Forms } from 'reactComponents/Forms';
import { connectFormikProps } from 'reactComponents/Forms/ConnectFormikProps';
import { ToastNotify } from 'reactComponents/ToastNotify/ToastNotify';

import styles from './AutoPayment.css';

export type AutoPaymentProps = {
    likeDialog?: boolean;
    onClose(): void;
    isActiveDefault?: boolean;
};

type Values = {
    isActive: boolean;
    sum4payment: number;
    limit2life: number;
};

const preConnector = ReduxTypeUtils.makeConnector((state) => {
    const { project_id: clientPid } = getActiveRouteParams(state);

    return {
        pid: CurrentUserSelectors.getPid(state),
        clientPid,
    };
});
type PreProps = ReduxTypeUtils.TypeOfConnector<typeof preConnector>;
const connector = ReduxTypeUtils.makeConnector(
    (state: GlobalState, ownProps: AutoPaymentProps) => {
        return {
            sum4payment: BillingSelectors.getData(state)?.extra.sum4payment,
            limit2life: BillingSelectors.getData(state)?.extra.limit2life,
            isActive: BillingSelectors.getAutopay(state),
        };
    },
    (props: PreProps) => ({
        enableAutoPay: BillingActions.enableAutoPay,
        disableAutoPay: BillingActions.disableAutoPay,
        updateBalance: getBillingActions(props.pid, props.clientPid).loadData,
    }),
);
type ConnectedProps = AutoPaymentProps &
    ReduxTypeUtils.TypeOfConnector<typeof connector>;

const formikConnector = connectFormikProps(
    ({
        sum4payment,
        limit2life,
        isActive,
        isActiveDefault,
    }: ConnectedProps) => {
        return {
            initialValues: {
                isActive: isActiveDefault || isActive,
                sum4payment: sum4payment ?? 1000,
                limit2life: limit2life ?? 100,
            },
        };
    },
);

type Props = AutoPaymentProps &
    ConnectedProps &
    ReduxTypeUtils.TypeOfConnector<typeof formikConnector> &
    Partial<DialogAsMethodRedererProps>;
type State = {
    isRequesting: boolean;
};
const SUM_MIN = 100;
const SUM_MAX = 2000;
const LIMIT_MIN = 0;
const LIMIT_MAX = 9999999;

class _AutoPayment extends React.PureComponent<Props> {
    static defaultProps = {
        likeDialog: false,
    };

    state: State = {
        isRequesting: false,
    };

    isUnmounted = false;

    constructor(props) {
        super(props);

        props.setSubmitHandler(this.handleSubmit);
        props.setValidation(this.validate);
    }

    componentWillUnmount() {
        this.isUnmounted = true;
    }

    validate = (values: Values) => {
        return Forms.Validation.validateSchema({
            values,
            validationSchema: this.getValidationSchema(),
        });
    };

    getValidationSchema = () => {
        const schema = {
            sum4payment: Forms.Validation.rub({
                min: SUM_MIN,
                max: SUM_MAX,
            }),
            limit2life: Forms.Validation.rub({
                min: LIMIT_MIN,
                max: LIMIT_MAX,
            }),
        };
        return Forms.Validation.createSchema(schema);
    };

    handleSubmit = async (values: Values) => {
        this.setState({ isRequesting: true });

        try {
            if (values.isActive) {
                await this.props.enableAutoPay({
                    limit2life: Number(values.limit2life),
                    sum4payment: Number(values.sum4payment),
                });
                await this.afterSubmit();
            } else if (this.props.isActive) {
                Confirmator.confirm({
                    title: 'Автопополнение',
                    text:
                        'Вы действительно хотите отключить автопополнение баланса?',
                    confirmButtonText: 'Отключить',
                    request: async (closeDialog) => {
                        try {
                            await this.props.disableAutoPay();
                            await this.afterSubmit();
                        } catch (error) {
                            this.onError(error);
                        } finally {
                            closeDialog();
                        }
                    },
                });
            }

            this.props.onClose();
        } catch (error) {
            this.onError(error);
        } finally {
            !this.isUnmounted && this.setState({ isRequesting: false });
        }
    };

    afterSubmit = async () => {
        await this.props.updateBalance();
        ToastNotify.success('Настройки автопополнения успешно сохранены');
    };

    onError = (error) => {
        console.error(error);
        ToastNotify.error('Не удалось изменить настройки автопополнения');
        !this.isUnmounted && this.setState({ isRequesting: false });
    };

    renderRubleIcon() {
        return <div className={styles.rubleIcon}>₽</div>;
    }

    renderAutoPaymentForm() {
        const { _formikParams: formikParams } = this.props;
        return (
            <div className={styles.content}>
                <AutoPaymentDescription />
                <Forms.FieldCheckbox
                    name="isActive"
                    controlLabel="Включить автопополнение баланса"
                />
                <Forms.FieldInput
                    label="Сумма автопополнения"
                    name="sum4payment"
                    type="number"
                    min={SUM_MIN}
                    max={SUM_MAX}
                    withoutButtons
                    step={0.01}
                    hidden={!formikParams.values.isActive}
                    rightIcon={this.renderRubleIcon()}
                />
                <Forms.FieldInput
                    label="Остаток на\u00A0балансе, при\u00A0котором происходит пополнение"
                    name="limit2life"
                    type="number"
                    min={LIMIT_MIN}
                    max={LIMIT_MAX}
                    withoutButtons
                    step={0.01}
                    hidden={!formikParams.values.isActive}
                    rightIcon={this.renderRubleIcon()}
                />
            </div>
        );
    }

    renderLikeDialog(autoPaymentForm: React.ReactNode) {
        const { isOpened, onClose } = this.props;

        return (
            <Dialog
                header="Настройка автопополнения"
                isRequesting={this.state.isRequesting}
                open={isOpened}
                onCancel={onClose}
                accept="Сохранить изменения"
                formikParams={this.props._formikParams}
                cancel="Отменить"
            >
                {autoPaymentForm}
            </Dialog>
        );
    }

    renderInline(autoPaymentForm: React.ReactNode) {
        const { _formikParams: formikParams } = this.props;

        return (
            <Forms.Form
                className={styles.form}
                onSubmit={formikParams.handleSubmit}
                error={formikParams.hasErrors}
            >
                {autoPaymentForm}

                <div className={styles.formFooter}>
                    <Button
                        primary
                        type="submit"
                        loading={this.state.isRequesting}
                        disabled={this.state.isRequesting}
                    >
                        Сохранить изменения
                    </Button>
                    <Button onClick={this.props.onClose}>Отменить</Button>
                </div>
            </Forms.Form>
        );
    }

    render() {
        const { likeDialog } = this.props;
        const autoPaymentForm = this.renderAutoPaymentForm();

        return likeDialog
            ? this.renderLikeDialog(autoPaymentForm)
            : this.renderInline(autoPaymentForm);
    }
}

export const AutoPayment = preConnector(
    connector(formikConnector(_AutoPayment)),
);

export function AutoPaymentDescription() {
    return (
        <div className={styles.description}>
            Автопополнение баланса привязанной картой позволит сохранить
            доступность услуг&nbsp;MCS в&nbsp;случае, если&nbsp;закончится
            оплаченный период.
        </div>
    );
}
